// src/components/wish/TemplateGenerator.jsx
import React, { useEffect, useState, useRef, useLayoutEffect } from "react";
import { useParams } from "react-router-dom";

/* ───────── helpers ───────── */
const DEFAULT_BG = "from-pink-100 via-purple-50 to-blue-100";
const DEFAULT_TEXT = "#1f2937";

const normalizeUrl = (u) => (typeof u === "string" ? u.replace(/\\\//g, "/") : "");
const num = (v, fb = 0) => {
    const n = typeof v === "string" ? parseFloat(v) : v;
    return Number.isFinite(n) ? n : fb;
};
const alignToClass = (align) =>
    align === "center" ? "text-center" : align === "right" ? "text-right" : "text-left";

/** Extract a media URL from a value that might be a plain string, JSON string, array, or object */
const extractMediaUrl = (bg) => {
    if (!bg) return "";
    if (Array.isArray(bg)) return bg[bg.length - 1] || "";
    if (typeof bg === "string") {
        const s = bg.trim();
        if (s.startsWith("[") || s.startsWith("{")) {
            try {
                const parsed = JSON.parse(s);
                if (Array.isArray(parsed) && parsed.length) return parsed[parsed.length - 1] || "";
                if (parsed?.url) return parsed.url;
            } catch {
                /* ignore */
            }
        }
        return s.replace(/^"(.*)"$/, "$1");
    }
    if (bg?.url) return bg.url;
    return "";
};

const resolvePlaceholders = (raw, { receiverName = "", senderName = "", message = "" }) =>
    String(raw ?? "")
        .replaceAll("{receiverName}", receiverName)
        .replaceAll("{senderName}", senderName)
        .replaceAll("{message}", message);

const textStyleToInline = (style, fallbackColor, scale = 1) => {
    const s = style || {};
    const scaledSize = (s.fontSize ?? 24) * scale;
    const baseLetter = typeof s.letterSpacing === "number" ? s.letterSpacing * scale : 1 * scale;
    return {
        color: s.gradient ? undefined : s.color || fallbackColor || DEFAULT_TEXT,
        fontSize: `${scaledSize}px`,
        fontWeight: s.fontWeight ?? 600,
        // Roomier default line height for better readability
        lineHeight: s.lineHeight ?? 1.6,
        // More generous default letter spacing to keep headings breathable
        letterSpacing: typeof s.letterSpacing === "number" ? `${baseLetter}px` : `${baseLetter}px`,
        fontStyle: s.italic ? "italic" : "normal",
        textTransform: s.uppercase ? "uppercase" : "none",
        textShadow: s.shadow ? "0 2px 12px rgba(0,0,0,.20)" : undefined,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        overflowWrap: "anywhere",
        display: "inline-block",
    };
};

const rowToBlock = (r) => ({
    id: String(r.id ?? Math.random()),
    type: r.block_type || "custom",
    default_text: r.default_text ?? "",
    text: r.content ?? r.text ?? "",
    style: {
        color: r.color ?? DEFAULT_TEXT,
        gradient: r.gradient || undefined,
        fontSize: num(r.font_size ?? 24, 24),
        fontWeight: num(r.weight ?? 600, 600),
        lineHeight: num(r.line_height ?? r.lineHeight ?? 1.5, 1.5),
        textAlign: r.align || "left",
        italic: !!r.italic,
        uppercase: !!r.uppercase,
        shadow: !!r.shadow,
    },
    x: num(r.position_x ?? r.x ?? 10, 10),
    y: num(r.position_y ?? r.y ?? 10, 10),
    w: num(r.width ?? 80, 80),
    rotate: num(r.rotate ?? 0, 0),
});

/* ───────── background media ───────── */
const MediaBackground = ({ url, opacity = 1, blurAmount = 0 }) => {
    if (!url) return null;
    const cleanUrl = normalizeUrl(url);
    const lower = cleanUrl.split("?")[0].toLowerCase();

    const common = {
        className: "absolute inset-0 w-full h-full object-contain",
        style: {
            opacity,
            filter: blurAmount ? `blur(${blurAmount}px)` : undefined,
        },
    };

    if (/\.(mp4|webm|mov)$/.test(lower)) {
        return <video {...common} src={cleanUrl} autoPlay loop muted playsInline />;
    }
    return <img {...common} src={cleanUrl} alt="Background" />;
};

/* ───────── full-bleed, top-anchored scaling ───────── */
function ScaledStage({ children, baseWidth = 800, baseHeight = 1200 }) {
    const ref = useRef(null);
    const [scale, setScale] = useState(1);
    const [isNarrow, setIsNarrow] = useState(false);

    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;
        const resize = () => {
            const { width, height } = el.getBoundingClientRect();
            const s = Math.min(width / baseWidth, height / baseHeight);
            setScale(s > 0 ? s : 1);
            setIsNarrow(Math.min(width, height) < 780);
        };
        resize();
        const ro = new ResizeObserver(resize);
        ro.observe(el);
        window.addEventListener("resize", resize);
        return () => {
            ro.disconnect();
            window.removeEventListener("resize", resize);
        };
    }, [baseWidth, baseHeight]);

    return (
        <div
            ref={ref}
            className="w-[100vw] h-[100dvh] flex items-center justify-center overflow-hidden m-0 p-0"
            style={{
                background: "transparent",
                position: "relative",
                height: isNarrow ? "100svh" : "100dvh",
                minHeight: isNarrow ? "100svh" : "100dvh",
            }}
        >
            <div
                style={{
                    width: baseWidth,
                    height: baseHeight,
                    transform: `scale(${scale})`,
                    transformOrigin: "center center",
                    borderRadius: 0,
                    boxShadow: "none",
                    background: "transparent",
                    position: "relative",
                }}
            >
                {children}
            </div>
        </div>
    );
}

/* ───────── main component ───────── */
const TemplateGenerator = ({
    // Optional pre-fetched data (WishPage can pass these to avoid any fetch)
    wish: wishProp,
    rows: rowsProp = [],

    // Or load via token from prop/URL (public) if props not provided
    wishToken: propWishToken,

    // Presentation options
    asModal = false,
    onClose,
    baseWidth = 800,
    baseHeight = 1200,
}) => {
    const { token: urlToken } = useParams();
    const wishToken = propWishToken || urlToken || null;

    const [loading, setLoading] = useState(!wishProp);
    const [wish, setWish] = useState(wishProp || null);
    const [blocks, setBlocks] = useState(wishProp ? rowsProp.map(rowToBlock) : []);
    const [mediaSize, setMediaSize] = useState({ width: baseWidth, height: baseHeight });

    /* Public fetch only when wish/rows were not provided */
    useEffect(() => {
        let cancel = false;
        if (wishProp) return;

        (async () => {
            if (!wishToken) return;
            setLoading(true);
            try {
                const res = await fetch(`/api/wish/showByToken/${encodeURIComponent(wishToken)}`, {
                    headers: { Accept: "application/json" },
                });

                if (!res.ok) {
                    if (!cancel) {
                        setWish(null);
                        setBlocks([]);
                    }
                    return;
                }

                const data = await res.json();
                const w = data?.wish ?? null;
                const rows = Array.isArray(data?.wishTexts) ? data.wishTexts : [];
                // prefer server-provided signed/public background URL if present
                if (w && (data?.bg_url || w?.bg_url)) {
                    w._bg_url = data.bg_url || w.bg_url;
                }

                if (!cancel) {
                    setWish(w);
                    setBlocks(rows.map(rowToBlock));
                }
            } finally {
                if (!cancel) setLoading(false);
            }
        })();

        return () => {
            cancel = true;
        };
    }, [wishProp, wishToken, rowsProp]);

    /* Derive template look with robust fallbacks.
       Order for background: server bg_url (public) -> template -> wish. */
    const t =
        wish?.template ??
        wish?.wishTemplate ??
        wish?.wish_template ??
        wish ??
        {};

    // Prefer the API's public/signed URL
    const bgFromApi = normalizeUrl(wish?._bg_url || "");
    const bgFromFields = extractMediaUrl(
        t.background_gif ?? t.backgroundGif ?? wish?.background_gif ?? wish?.backgroundGif
    );
    const backgroundGif = bgFromApi || bgFromFields;

    // Match the canvas dimensions to the intrinsic media size when available
    useEffect(() => {
        let canceled = false;
        if (!backgroundGif) {
            setMediaSize({ width: baseWidth, height: baseHeight });
            return;
        }

        const cleanUrl = normalizeUrl(backgroundGif);
        const lower = cleanUrl.split("?")[0].toLowerCase();

        if (/\.(mp4|webm|mov)$/.test(lower)) {
            const video = document.createElement("video");
            const onMeta = () => {
                if (canceled) return;
                if (video.videoWidth && video.videoHeight) {
                    setMediaSize({ width: video.videoWidth, height: video.videoHeight });
                }
            };
            video.addEventListener("loadedmetadata", onMeta);
            video.preload = "metadata";
            video.src = cleanUrl;
            video.muted = true;
            video.playsInline = true;
            video.load();
            return () => {
                canceled = true;
                video.removeEventListener("loadedmetadata", onMeta);
                video.src = "";
            };
        }

        const img = new Image();
        img.onload = () => {
            if (canceled) return;
            if (img.naturalWidth && img.naturalHeight) {
                setMediaSize({ width: img.naturalWidth, height: img.naturalHeight });
            }
        };
        img.onerror = () => {
            if (canceled) return;
            setMediaSize({ width: baseWidth, height: baseHeight });
        };
        img.src = cleanUrl;
        return () => {
            canceled = true;
            img.onload = null;
            img.onerror = null;
        };
    }, [backgroundGif, baseWidth, baseHeight]);

    const tpl = {
        backgroundColor:
            t.background_color || t.backgroundColor || wish?.background_color || DEFAULT_BG,
        backgroundGif,
        textColor: t.text_color || t.textColor || DEFAULT_TEXT,
        bgOpacity: (() => {
            const val =
                typeof t.background_opacity === "number"
                    ? t.background_opacity
                    : typeof t.bg_opacity === "number"
                        ? t.bg_opacity
                        : undefined;
            // If there's media, default to 1 so it’s clearly visible publicly.
            if (typeof val === "number") return val;
            return backgroundGif ? 1 : 0.28;
        })(),
        bgBlurAmount:
            typeof t.background_blur === "number"
                ? t.background_blur
                : typeof t.bg_blur_amount === "number"
                    ? t.bg_blur_amount
                    : 0,
    };

    const vars = {
        receiverName: wish?.receiver_name || "",
        senderName: wish?.sender_name || "",
        message: wish?.message || "",
    };

    const canvasWidth = mediaSize.width || baseWidth;
    const canvasHeight = mediaSize.height || baseHeight;
    const scaleFactor = Math.max(0.65, Math.min(1, canvasHeight / 1000)); // shrink text on small canvases
    const baseMarginTop = 8;
    const dynamicLineMargin = () => {
        // Scale margin by media size so taller GIFs naturally get more breathing room
        const margin = canvasHeight * 0.02; // ~2% of height
        return Math.max(10, Math.min(30, margin));
    };
    const verticalGapPx = Math.min(40, Math.max(6, canvasHeight * 0.015)); // scale gap with media height, smaller
    const verticalGapPercent = canvasHeight ? (verticalGapPx / canvasHeight) * 100 : 3;
    const gradient = tpl.backgroundColor ? `bg-gradient-to-br ${tpl.backgroundColor}` : "bg-white";

    const Canvas = (
        <div className={`relative ${gradient}`} style={{ width: canvasWidth, height: canvasHeight }}>
            {/* Background media (full opacity by default if present) */}
            <MediaBackground url={tpl.backgroundGif} opacity={tpl.bgOpacity} blurAmount={tpl.bgBlurAmount} />

            {/* Text blocks */}
            <div className="absolute inset-0">
                {blocks.map((b, idx) => {
                    const styleBox = {
                        left: `${num(b.x, 0)}%`,
                        top: `${num(b.y, 0) + verticalGapPercent * idx}%`,
                        width: `${num(b.w, 100)}%`,
                        transform: `rotate(${num(b.rotate, 0)}deg)`,
                    };
                    const inline = textStyleToInline(b.style, tpl.textColor, scaleFactor);
                    const clsAlign = alignToClass(b.style?.textAlign);
                    const content =
                        b.text && String(b.text).trim().length > 0
                            ? b.text
                            : resolvePlaceholders(b.default_text, vars);
                    const contentLowerRaw = String(content).trim().toLowerCase();
                    const contentLower = contentLowerRaw.replace(/"/g, "");
                    const marginTopValue =
                        inline.marginTop ??
                        (contentLower === "birthday!"
                            ? `${baseMarginTop + dynamicLineMargin()}px`
                            : contentLower === "abcd efgh"
                                ? `${baseMarginTop + dynamicLineMargin()}px`
                                : `${baseMarginTop}px`);
                    const inlineWithMargin = {
                        ...inline,
                        // Base top margin for all text plus extra breathing room for the "Birthday!" line
                        marginTop: marginTopValue,
                    };

                    return (
                        <div key={b.id} className={`absolute ${clsAlign}`} style={styleBox}>
                            <span
                                className={
                                    b.style?.gradient
                                        ? `bg-gradient-to-r ${b.style.gradient} bg-clip-text text-transparent`
                                        : ""
                                }
                                style={inlineWithMargin}
                            >
                                {content}
                            </span>
                        </div>
                    );
                })}

                {!loading && blocks.length === 0 && (
                    <div className="absolute inset-0 flex items-center justify-center text-gray-500">
                        No design blocks found for this wish.
                    </div>
                )}
            </div>
        </div>
    );

    if (!asModal) {
        // Full-screen, no side margins; top-anchored scaled canvas
        return (
            <div className="fixed inset-0 m-0 p-0 overflow-hidden">
                {/* Full-bleed fallback background to avoid white bars when canvas is letterboxed */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div className={`absolute inset-0 ${gradient}`} />
                    <MediaBackground
                        url={tpl.backgroundGif}
                        opacity={Math.min(tpl.bgOpacity ?? 1, 0.35)}
                        blurAmount={Math.max(tpl.bgBlurAmount ?? 0, 12)}
                    />
                </div>
                <ScaledStage baseWidth={canvasWidth} baseHeight={canvasHeight}>
                    {Canvas}
                </ScaledStage>
            </div>
        );
    }

    // Modal variant
    return (
        <div className="fixed inset-0 z-[9999]" role="dialog" aria-modal="true">
            <button
                type="button"
                aria-label="Close preview"
                onClick={() => onClose?.()}
                className="absolute inset-0 bg-black/50 backdrop-blur-md focus:outline-none"
            />
            <div className="relative z-10 w-full h-full">
                <ScaledStage baseWidth={canvasWidth} baseHeight={canvasHeight}>
                    {Canvas}
                </ScaledStage>
                <button
                    onClick={() => onClose?.()}
                    className="absolute right-3 top-3 z-20 inline-flex items-center justify-center rounded-full p-2 bg-white/80 hover:bg-white shadow"
                    aria-label="Close"
                >
                    ✕
                </button>
            </div>
        </div>
    );
};

export default TemplateGenerator;
